export type TenantPermission =
  | 'qr.view'
  | 'qr.manage'
  | 'menu.view'
  | 'menu.manage'
  | 'orders.view'
  | 'orders.manage'
  | 'orders.fulfill'
  | 'catering.view'
  | 'catering.manage'
  | 'payments.manage'
  | 'reports.view'
  | 'staff.manage'
  | 'settings.manage'
  | 'integrations.manage'
  | 'audit.view'
  | 'payroll.bridge.view'
  | 'payroll.bridge.manage'
  | 'payroll.bridge.ess'
  | 'payroll.bridge.ssn_unmask';

export type TenantRole =
  | 'owner'
  | 'manager'
  | 'cashier'
  | 'cook'
  | 'expo'
  | 'support'
  | 'platform_admin'
  | 'payroll_admin'
  | 'employee';

export type ClientTenantRole =
  | 'OWNER'
  | 'MANAGER'
  | 'CASHIER'
  | 'COOK'
  | 'EXPO'
  | 'SUPPORT'
  | 'PLATFORM_ADMIN'
  | 'PAYROLL_ADMIN'
  | 'EMPLOYEE';

interface RoleDefinition {
  permissions: TenantPermission[];
  inherits?: TenantRole[];
}

const ROLE_DEFINITIONS: Record<TenantRole, RoleDefinition> = {
  platform_admin: {
    permissions: [
      'qr.view',
      'qr.manage',
      'menu.view',
      'menu.manage',
      'orders.view',
      'orders.manage',
      'orders.fulfill',
      'catering.view',
      'catering.manage',
      'payments.manage',
      'reports.view',
      'staff.manage',
      'settings.manage',
      'integrations.manage',
      'audit.view',
      'payroll.bridge.view',
      'payroll.bridge.manage',
      'payroll.bridge.ssn_unmask',
    ],
  },
  owner: {
    permissions: [
      'qr.view',
      'qr.manage',
      'menu.view',
      'menu.manage',
      'orders.view',
      'orders.manage',
      'orders.fulfill',
      'catering.view',
      'catering.manage',
      'payments.manage',
      'reports.view',
      'staff.manage',
      'settings.manage',
      'integrations.manage',
      'audit.view',
      'payroll.bridge.view',
      'payroll.bridge.manage',
    ],
  },
  manager: {
    inherits: ['support'],
    permissions: [
      'qr.view',
      'qr.manage',
      'menu.view',
      'menu.manage',
      'orders.view',
      'orders.manage',
      'orders.fulfill',
      'catering.view',
      'catering.manage',
      'payments.manage',
      'reports.view',
      'staff.manage',
      'settings.manage',
      'integrations.manage',
      'audit.view',
      'payroll.bridge.view',
      'payroll.bridge.manage',
    ],
  },
  cashier: {
    inherits: ['support'],
    permissions: ['orders.manage', 'catering.view', 'payments.manage'],
  },
  cook: {
    inherits: ['support'],
    permissions: ['orders.fulfill'],
  },
  expo: {
    inherits: ['support'],
    permissions: ['orders.view', 'orders.fulfill', 'reports.view'],
  },
  support: {
    permissions: ['qr.view', 'menu.view', 'orders.view', 'catering.view', 'reports.view'],
  },
  payroll_admin: {
    inherits: ['manager'],
    permissions: [
      'payroll.bridge.view',
      'payroll.bridge.manage',
      'payroll.bridge.ssn_unmask',
      'audit.view',
    ],
  },
  employee: {
    permissions: ['payroll.bridge.view', 'payroll.bridge.ess'],
  },
};

const ROLE_ALIASES: Record<string, TenantRole> = {
  owner: 'owner',
  manager: 'manager',
  cashier: 'cashier',
  cook: 'cook',
  kitchen: 'cook',
  expo: 'expo',
  waiter: 'expo',
  runner: 'expo',
  support: 'support',
  staff: 'support',
  viewer: 'support',
  agent: 'support',
  admin: 'owner',
  platform_admin: 'platform_admin',
  platformadmin: 'platform_admin',
  platform: 'platform_admin',
  payroll_admin: 'payroll_admin',
  payrolladmin: 'payroll_admin',
  payroll: 'payroll_admin',
  employee: 'employee',
  ess: 'employee',
};

const normaliseRoleInput = (role: string | null | undefined): string | null => {
  if (!role) {
    return null;
  }
  return role.trim().toLowerCase().replace(/[\s-]+/g, '_');
};

export const resolveTenantRole = (role: string | null | undefined): TenantRole | null => {
  const normalized = normaliseRoleInput(role);
  if (!normalized) {
    return null;
  }
  return ROLE_ALIASES[normalized] ?? null;
};

const collectPermissions = (
  role: TenantRole,
  visited = new Set<TenantRole>(),
): Set<TenantPermission> => {
  if (visited.has(role)) {
    return new Set();
  }
  visited.add(role);

  const definition = ROLE_DEFINITIONS[role];
  const permissions = new Set<TenantPermission>(definition.permissions);

  for (const inherited of definition.inherits ?? []) {
    const inheritedPermissions = collectPermissions(inherited, visited);
    inheritedPermissions.forEach((permission) => permissions.add(permission));
  }

  return permissions;
};

export const resolveRolePermissions = (
  role: string | TenantRole | ClientTenantRole | null | undefined,
): Set<TenantPermission> => {
  if (!role) {
    return new Set();
  }

  const resolved =
    typeof role === 'string' ? resolveTenantRole(role) : resolveTenantRole(String(role));

  if (!resolved) {
    return new Set();
  }

  return collectPermissions(resolved);
};

const CLIENT_ROLE_MAP: Record<TenantRole, ClientTenantRole> = {
  platform_admin: 'PLATFORM_ADMIN',
  owner: 'OWNER',
  manager: 'MANAGER',
  cashier: 'CASHIER',
  cook: 'COOK',
  expo: 'EXPO',
  support: 'SUPPORT',
  payroll_admin: 'PAYROLL_ADMIN',
  employee: 'EMPLOYEE',
};

const INVERSE_CLIENT_ROLE_MAP: Record<ClientTenantRole, TenantRole> = {
  PLATFORM_ADMIN: 'platform_admin',
  OWNER: 'owner',
  MANAGER: 'manager',
  CASHIER: 'cashier',
  COOK: 'cook',
  EXPO: 'expo',
  SUPPORT: 'support',
  PAYROLL_ADMIN: 'payroll_admin',
  EMPLOYEE: 'employee',
};

export const tenantRoleToClientRole = (role: TenantRole): ClientTenantRole =>
  CLIENT_ROLE_MAP[role];

export const clientRoleToTenantRole = (role: ClientTenantRole): TenantRole =>
  INVERSE_CLIENT_ROLE_MAP[role];

export const resolveClientTenantRole = (
  role: string | null | undefined,
): ClientTenantRole | null => {
  const resolved = resolveTenantRole(role);
  return resolved ? tenantRoleToClientRole(resolved) : null;
};

export const listRolePermissions = (role: TenantRole | ClientTenantRole): TenantPermission[] =>
  Array.from(resolveRolePermissions(role));

export const ALL_TENANT_PERMISSIONS: TenantPermission[] = Array.from(
  new Set(
    Object.keys(ROLE_DEFINITIONS).flatMap((key) =>
      listRolePermissions(key as TenantRole),
    ),
  ),
);
